import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "trial-banner__content" }
const _hoisted_2 = { class: "trial-banner__info" }
const _hoisted_3 = { class: "trial-banner__badge" }
const _hoisted_4 = { class: "trial-banner__message" }
const _hoisted_5 = { class: "trial-banner__title" }
const _hoisted_6 = { class: "trial-banner__subtitle" }
const _hoisted_7 = { class: "trial-banner__actions" }

import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/Button.vue";
import { useBillingStatus } from "@/composables/billing/useBillingStatus";
import {
  CREATE_SETUP_CHECKOUT,
  GET_SUBSCRIPTION_STATUS,
  GET_TRIAL_STATUS,
} from "@/store/action-types";

const DISMISS_KEY = "trialBannerDismissed";
const DISMISS_DURATION = 24 * 60 * 60 * 1000;


export default /*@__PURE__*/_defineComponent({
  __name: 'TrialBanner',
  setup(__props) {

const store = useStore();
const router = useRouter();
const isDismissed = ref(false);

const isTrialing = computed(() => store.getters.isTrialing);
const hasPaymentMethod = computed(() => store.getters.hasPaymentMethod);
const isFreeTrialActive = computed(() => store.getters.isFreeTrialActive);
const trialStatus = computed(() => store.getters.trialStatus?.data);

const subscriptionStatus = computed(() => store.getters.subscriptionStatus?.data);

const { isUsageBlocked } = useBillingStatus();

const daysLeft = computed(() => {
  if (isFreeTrialActive.value && trialStatus.value?.daysRemaining !== undefined) {
    return trialStatus.value.daysRemaining;
  }
  return store.getters.daysLeftInTrial;
});

const bannerType = computed(() => {
  if (isUsageBlocked.value) return null;
  // Never show trial banner if user has an active subscription
  if (subscriptionStatus.value?.hasSubscription && subscriptionStatus.value?.status === "active") {
    return null;
  }
  if (isFreeTrialActive.value) return "free_trial";
  if (isTrialing.value && !hasPaymentMethod.value) return "stripe_trial";
  return null;
});

const showBanner = computed(() => {
  return bannerType.value !== null && !isDismissed.value && daysLeft.value > 0;
});

const bannerIcon = computed(() => {
  return bannerType.value === "free_trial" ? "mdi:rocket-outline" : "mdi:clock-outline";
});

const bannerTitle = computed(() => {
  return bannerType.value === "free_trial" ? "Free Trial" : "Trial Period";
});

const bannerSubtitle = computed(() => {
  return bannerType.value === "free_trial"
    ? "Upgrade for full access to calls, SMS, enrichment & more"
    : "Add a payment method to continue after your trial";
});

const bannerCTA = computed(() => {
  return bannerType.value === "free_trial" ? "Upgrade Now" : "Add Payment Method";
});

const ctaIcon = computed(() => {
  return bannerType.value === "free_trial"
    ? "mdi:arrow-up-circle-outline"
    : "mdi:credit-card-plus-outline";
});

const checkDismissed = () => {
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (dismissedAt) {
    const dismissedTime = parseInt(dismissedAt, 10);
    if (Date.now() - dismissedTime < DISMISS_DURATION) {
      isDismissed.value = true;
      return;
    }
    localStorage.removeItem(DISMISS_KEY);
  }
  isDismissed.value = false;
};

const dismissBanner = () => {
  localStorage.setItem(DISMISS_KEY, Date.now().toString());
  isDismissed.value = true;
};

const handleCTAClick = async () => {
  if (bannerType.value === "free_trial") {
    router.push("/subscription-wall");
  } else {
    sessionStorage.setItem("sb_payment_setup_return_path", window.location.pathname);
    const data = await store.dispatch(CREATE_SETUP_CHECKOUT, {
      returnPath: window.location.pathname,
    });
    if (data?.url) {
      window.location.href = data.url;
    }
  }
};

onMounted(() => {
  checkDismissed();
});

watch(
  () => store.getters.account?.data?.id,
  async (accountId, oldAccountId) => {
    if (accountId && !oldAccountId) {
      await Promise.all([
        store.dispatch(GET_SUBSCRIPTION_STATUS),
        store.dispatch(GET_TRIAL_STATUS),
      ]);
    }
  }
);

watch(
  () => store.getters.subscriptionStatus,
  () => {
    if (hasPaymentMethod.value) {
      localStorage.removeItem(DISMISS_KEY);
      isDismissed.value = false;
    }
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "banner" }, {
    default: _withCtx(() => [
      (showBanner.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["trial-banner", `trial-banner--${bannerType.value}`])
          }, [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(_unref(Icon), { icon: bannerIcon.value }, null, 8, ["icon"]),
                  _createElementVNode("span", null, _toDisplayString(daysLeft.value) + " day" + _toDisplayString(daysLeft.value === 1 ? "" : "s") + " left", 1)
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(bannerTitle.value), 1),
                  _createElementVNode("span", _hoisted_6, _toDisplayString(bannerSubtitle.value), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createVNode(Button, {
                  variant: "secondary",
                  size: "xs",
                  icon: ctaIcon.value,
                  class: "trial-banner__cta",
                  onClick: handleCTAClick
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(bannerCTA.value), 1)
                  ]),
                  _: 1
                }, 8, ["icon"]),
                _createElementVNode("button", {
                  class: "trial-banner__dismiss",
                  onClick: dismissBanner,
                  "aria-label": "Dismiss"
                }, [
                  _createVNode(_unref(Icon), { icon: "mdi:close" })
                ])
              ])
            ])
          ], 2))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})